import React, { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { SprintPacketSurveyStatus } from '@marketing-milk/interfaces'
import { radius, SurfacePalette, type } from '../../tokens'
import { SectionCard } from './SectionCard'

interface Props {
  palette: SurfacePalette
  packetID: number
  survey: SprintPacketSurveyStatus
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

function Badge({ palette, children }: { palette: SurfacePalette; children: React.ReactNode }) {
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 999,
        border: `1px solid ${palette.hairline}`,
        background: palette.bg,
        color: palette.textMuted,
        fontFamily: type.family.mono,
        fontSize: 10,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  )
}

// Edit-mode home of the v1.7 client survey: the QR/link the client scans, plus
// the status-only response list. Answers are never present in the hydrated
// packet, so there is nothing more to show here by construction.
export function ClientSurveySection({ palette, packetID, survey }: Props) {
  const [copied, setCopied] = useState(false)
  const expired = new Date(survey.expiresAt).getTime() < Date.now()

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(survey.link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard can be unavailable (permissions, http) — the link is visible
      // below the button, so a failed copy still leaves a manual path.
    }
  }

  return (
    <SectionCard
      palette={palette}
      sectionKey="client-survey"
      title="Client Survey"
      commentAnchor={{ type: 'section', id: `${packetID}:client-survey`, label: 'Client Survey' }}
    >
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <div
            style={{
              padding: 12,
              borderRadius: radius.md,
              border: `1px solid ${palette.hairline}`,
              background: '#fff',
            }}
          >
            <QRCodeSVG value={survey.link} size={144} />
          </div>
          <button
            type="button"
            onClick={copyLink}
            style={{
              padding: '6px 14px',
              borderRadius: radius.sm,
              border: `1px solid ${palette.hairline}`,
              background: palette.surface,
              color: palette.text,
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          <div
            style={{
              fontFamily: type.family.sans,
              fontSize: type.size.eyebrow,
              color: expired ? '#d64545' : palette.textFaint,
            }}
          >
            {expired
              ? `Link expired ${formatDate(survey.expiresAt)}`
              : `Link valid through ${formatDate(survey.expiresAt)}`}
          </div>
        </div>

        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <div
            style={{
              fontFamily: type.family.mono,
              fontSize: type.size.eyebrow,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: palette.textMuted,
              marginBottom: 10,
            }}
          >
            Responses · {survey.responses.length}
          </div>
          {survey.responses.length === 0 ? (
            <p
              style={{
                margin: 0,
                fontFamily: type.family.sans,
                fontSize: type.size.body,
                fontStyle: 'italic',
                color: palette.textFaint,
              }}
            >
              No responses yet — the client scans the QR (or opens the link) on their own phone.
              Answers stay private; only who responded shows here.
            </p>
          ) : (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {survey.responses.map((response, i) => (
                <li
                  key={`${response.submittedAt}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    padding: '8px 12px',
                    borderRadius: radius.sm,
                    border: `1px solid ${palette.hairline}`,
                    background: palette.bg,
                  }}
                >
                  <span
                    style={{
                      fontFamily: type.family.sans,
                      fontSize: type.size.body,
                      fontWeight: type.weight.semibold,
                      color: palette.text,
                    }}
                  >
                    {response.name}
                  </span>
                  {response.isDecisionMaker && <Badge palette={palette}>Decision maker</Badge>}
                  {response.strategistAssisted && <Badge palette={palette}>With strategist</Badge>}
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontFamily: type.family.sans,
                      fontSize: type.size.eyebrow,
                      color: palette.textFaint,
                    }}
                  >
                    {formatDateTime(response.submittedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SectionCard>
  )
}
